import { useEffect, useState } from "react";
import { GardienBadge } from "@/components/GardienBadge";
import { useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LOGO } from "@/data/exercises";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Check, CalendarCheck, Loader2, Download, Receipt, Building2, User, CreditCard } from "lucide-react";
import { toast } from "sonner";

const PLAN_BENEFITS = {
  particulier: ["Pour 1 proche", "Accès complet aux ateliers", "Suivi + espace famille", "Portrait-robot dans le style des Gardiens"],
  institution: ["Plusieurs résidents", "Accès complet aux ateliers", "Suivi + espace famille", "Portrait-robot dans le style des Gardiens"],
};

export default function Subscribe() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [receipts, setReceipts] = useState([]);
  const [billing, setBilling] = useState({ billing_name: "", vat_number: "", billing_address: "", billing_email: "" });
  const [savingBilling, setSavingBilling] = useState(false);
  const [stripeLoading, setStripeLoading] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cfg }, { data: s }, { data: rc }, { data: bp }] = await Promise.all([
          api.get("/paypal/config"), api.get("/paypal/subscription"),
          api.get("/paypal/receipts"), api.get("/billing-profile"),
        ]);
        setConfig(cfg); setSub(s); setReceipts(rc || []);
        setBilling({ billing_name: bp.billing_name || "", vat_number: bp.vat_number || "", billing_address: bp.billing_address || "", billing_email: bp.billing_email || "" });
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally { setLoading(false); }
    })();
  }, []);

  const saveBilling = async () => {
    setSavingBilling(true);
    try {
      const { data } = await api.put("/billing-profile", billing);
      setBilling({ billing_name: data.billing_name || "", vat_number: data.vat_number || "", billing_address: data.billing_address || "", billing_email: data.billing_email || "" });
      toast.success("Informations de facturation enregistrées.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Enregistrement impossible."); }
    finally { setSavingBilling(false); }
  };

  const downloadReceipt = async (no) => {
    try {
      const res = await api.get(`/paypal/receipts/${no}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `recu-${no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Impossible de télécharger le reçu."); }
  };

  const createOrder = (plan) => async () => {
    const { data } = await api.post("/paypal/orders", { plan });
    return data.id;
  };
  const onApprove = async (data) => {
    try {
      const { data: res } = await api.post(`/paypal/orders/${data.orderID}/capture`);
      if (res.paid) { toast.success("Merci ! Votre abonnement est activé."); setSub({ active: true, paid: true, subscribed: true, active_until: res.subscription_active_until }); }
      else toast.error("Le paiement n'a pas pu être finalisé.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Paiement échoué."); }
  };

  const payStripe = async (plan) => {
    setStripeLoading(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Paiement impossible."); setStripeLoading(""); }
  };

  const PlanCard = ({ plan }) => (
    <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-7 flex flex-col" data-testid={`plan-card-${plan.id}`}>
      <div className="flex items-center gap-2 mb-2">
        {plan.id === "institution" ? <Building2 className="w-5 h-5 text-emerald-700" /> : <User className="w-5 h-5 text-emerald-700" />}
        <h3 className="text-xl font-bold text-gray-800">{plan.title}</h3>
      </div>
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-4xl font-extrabold text-emerald-700">{plan.amount}&nbsp;€</span>
        <span className="text-gray-500">{plan.cadence}</span>
      </div>
      <ul className="space-y-2 mb-6">
        {(PLAN_BENEFITS[plan.id] || []).map((b) => (
          <li key={b} className="flex items-center gap-2 text-sm text-gray-700">
            <Check className="w-4 h-4 text-emerald-600 shrink-0" /> {b}
          </li>
        ))}
      </ul>
      <div className="mt-auto space-y-3">
        {config?.stripe_enabled && (
          <Button onClick={() => payStripe(plan.id)} disabled={stripeLoading === plan.id}
            className="w-full h-12 rounded-xl bg-emerald-700 hover:bg-emerald-800" data-testid={`stripe-checkout-${plan.id}`}>
            {stripeLoading === plan.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" /> Payer par carte</>}
          </Button>
        )}
        {config?.configured && (
          <div data-testid={`paypal-${plan.id}`}>
            <PayPalButtons style={{ layout: "vertical", shape: "pill", color: "gold", label: "paypal", height: 40 }}
              forceReRender={[plan.id]} createOrder={createOrder(plan.id)} onApprove={onApprove}
              onError={() => toast.error("Une erreur PayPal est survenue.")} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col font-care">
      <header className="flex items-center justify-between p-6">
        <img src={LOGO} alt="Passerelle" className="h-11 w-auto" />
        <Button variant="ghost" size="sm" onClick={() => navigate("/espaces")} data-testid="subscribe-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
      </header>

      <main className="flex-1 flex flex-col items-center px-6 pb-16 gap-8">
        <div className="text-center max-w-2xl mt-4">
          <h1 className="font-serif-display text-4xl sm:text-5xl font-normal text-gray-800 mb-2">Choisissez votre formule</h1>
          <p className="text-lg text-gray-500">Pour un particulier ou pour un établissement.</p>
        </div>
        <GardienBadge id="adele" eyebrow="Gardienne de vos repères" className="w-full max-w-3xl" testId="subscribe-gardien" />

        {!loading && sub?.free && !sub?.paid && (
          <div className="w-full max-w-3xl rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-emerald-800" data-testid="subscribe-free-info">
            <div className="flex items-center gap-2 font-bold mb-1"><CalendarCheck className="w-5 h-5" /> Accès offert : espace Aidant & Famille</div>
            <p className="text-sm">Votre achat sur passerelle.site vous donne accès gratuitement à l'espace Aidant & Famille. Pour l'espace Soignant & Patient et le mode patient, choisissez une formule ci-dessous.</p>
          </div>
        )}
        {!loading && sub?.trial && (
          <div className="w-full max-w-3xl rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-emerald-800" data-testid="subscribe-trial-info">
            <div className="flex items-center gap-2 font-bold mb-1"><CalendarCheck className="w-5 h-5" /> Essai gratuit en cours</div>
            <p className="text-sm">{`Vous profitez de tout Passerelle gratuitement jusqu'au ${new Date(sub.trial_until).toLocaleDateString("fr-FR")}. Aucune carte bancaire n'a été demandée : choisissez une formule quand vous le souhaitez.`}</p>
          </div>
        )}
        {!loading && sub?.trial_expired && (
          <div className="w-full max-w-3xl rounded-2xl bg-amber-50 border border-amber-300 p-5 text-amber-900" data-testid="subscribe-trial-expired">
            <div className="font-bold mb-1">Votre essai gratuit est terminé</div>
            <p className="text-sm">Vos profils et votre suivi sont conservés. Choisissez une formule pour retrouver l'espace Soignant & Patient.</p>
          </div>
        )}
        {!loading && !sub?.subscribed && (
          <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-200 bg-white" data-testid="plans-comparison">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-emerald-50 text-emerald-800">
                  <th className="text-left font-semibold px-4 py-3">Comparatif</th>
                  <th className="font-semibold px-4 py-3">Particulier</th>
                  <th className="font-semibold px-4 py-3">Institution</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-t border-gray-100"><td className="px-4 py-3 text-gray-500">Nombre de proches</td><td className="text-center px-4 py-3 font-semibold">1 seul</td><td className="text-center px-4 py-3 font-semibold">Illimité</td></tr>
                <tr className="border-t border-gray-100 bg-gray-50/50"><td className="px-4 py-3 text-gray-500">Tarif</td><td className="text-center px-4 py-3">30&nbsp;€ / semestre</td><td className="text-center px-4 py-3">70&nbsp;€ / mois</td></tr>
                <tr className="border-t border-gray-100"><td className="px-4 py-3 text-gray-500">Ateliers cognitifs & mobilité</td><td className="text-center px-4 py-3 text-emerald-600">✓</td><td className="text-center px-4 py-3 text-emerald-600">✓</td></tr>
                <tr className="border-t border-gray-100 bg-gray-50/50"><td className="px-4 py-3 text-gray-500">Suivi & espace famille</td><td className="text-center px-4 py-3 text-emerald-600">✓</td><td className="text-center px-4 py-3 text-emerald-600">✓</td></tr>
                <tr className="border-t border-gray-100"><td className="px-4 py-3 text-gray-500">Idéal pour</td><td className="text-center px-4 py-3">Une famille</td><td className="text-center px-4 py-3">EHPAD / structure</td></tr>
              </tbody>
            </table>
          </div>
        )}

        {loading ? (
          <Loader2 className="w-8 h-8 animate-spin text-emerald-700 mt-10" />
        ) : sub?.subscribed ? (
          <div className="w-full max-w-md bg-white rounded-3xl border border-gray-200 shadow-sm p-8" data-testid="subscribe-card">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-emerald-800" data-testid="subscribe-active">
              <div className="flex items-center gap-2 font-bold mb-1">
                <CalendarCheck className="w-5 h-5" /> Abonnement actif
              </div>
              <p className="text-sm">{`Valable jusqu'au ${sub.active_until ? new Date(sub.active_until).toLocaleDateString("fr-FR") : ""}.`}</p>
            </div>
          </div>
        ) : error ? (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="subscribe-error">{error}</div>
        ) : (!config?.configured && !config?.stripe_enabled) ? (
          <div className="w-full max-w-md rounded-2xl bg-amber-50 border border-amber-200 p-5 text-amber-800 text-sm" data-testid="subscribe-not-configured">
            Aucun moyen de paiement n'est configuré pour le moment.
          </div>
        ) : (
          config?.configured ? (
            <PayPalScriptProvider options={{ clientId: config.client_id, currency: "EUR", intent: "capture", components: "buttons", "disable-funding": "card,paylater,credit" }}>
              <div className="w-full max-w-3xl grid md:grid-cols-2 gap-6" data-testid="subscribe-card">
                {(config?.plans || []).map((p) => <PlanCard key={p.id} plan={p} />)}
              </div>
            </PayPalScriptProvider>
          ) : (
            <div className="w-full max-w-3xl grid md:grid-cols-2 gap-6" data-testid="subscribe-card">
              {(config?.plans || []).map((p) => <PlanCard key={p.id} plan={p} />)}
            </div>
          )
        )}

        {!loading && (
          <div className="w-full max-w-3xl bg-white rounded-3xl border border-gray-200 shadow-sm p-6 sm:p-8" data-testid="billing-profile">
            <h2 className="font-serif-display text-lg font-normal text-gray-800 mb-1 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-700" /> Informations de facturation
            </h2>
            <p className="text-sm text-gray-400 mb-4">Ces informations apparaîtront sur vos reçus PDF (facultatif).</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="billing_name">Nom de l'établissement</Label>
                <Input id="billing_name" value={billing.billing_name} onChange={(e) => setBilling((b) => ({ ...b, billing_name: e.target.value }))}
                  placeholder="EHPAD Les Tilleuls" className="mt-1 h-11" data-testid="billing-name-input" />
              </div>
              <div>
                <Label htmlFor="vat_number">N° de TVA (optionnel)</Label>
                <Input id="vat_number" value={billing.vat_number} onChange={(e) => setBilling((b) => ({ ...b, vat_number: e.target.value }))}
                  placeholder="FR 12 345678901" className="mt-1 h-11" data-testid="billing-vat-input" />
              </div>
            </div>
            <div className="mt-4">
              <Label htmlFor="billing_address">Adresse de l'établissement</Label>
              <Input id="billing_address" value={billing.billing_address} onChange={(e) => setBilling((b) => ({ ...b, billing_address: e.target.value }))}
                placeholder="173 rue de Courcelles, 75017 Paris, France" className="mt-1 h-11" data-testid="billing-address-input" />
            </div>
            <div className="mt-4">
              <Label htmlFor="billing_email">E-mail de l'établissement (optionnel)</Label>
              <Input id="billing_email" type="email" value={billing.billing_email} onChange={(e) => setBilling((b) => ({ ...b, billing_email: e.target.value }))}
                placeholder="direction@ehpad-les-tilleuls.fr" className="mt-1 h-11" data-testid="billing-email-input" />
              <p className="text-xs text-gray-400 mt-1">Il reçoit aussi le rappel de fin d'essai.</p>
            </div>
            <Button onClick={saveBilling} disabled={savingBilling} className="mt-4 h-11 bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="billing-save-button">
              {savingBilling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </div>
        )}

        {!loading && receipts.length > 0 && (
          <div className="w-full max-w-3xl bg-white rounded-3xl border border-gray-200 shadow-sm p-6 sm:p-8" data-testid="receipts-history">
            <h2 className="font-serif-display text-lg font-normal text-gray-800 mb-4 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-700" /> Historique des reçus
            </h2>
            <ul className="divide-y divide-gray-100">
              {receipts.map((r) => (
                <li key={r.receipt_no} className="flex items-center gap-4 py-3" data-testid="receipt-row">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 truncate">Reçu n° {r.receipt_no}</p>
                    <p className="text-sm text-gray-400">{r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : ""} · {r.amount} {r.currency}</p>
                  </div>
                  <button onClick={() => downloadReceipt(r.receipt_no)} data-testid={`receipt-download-${r.receipt_no}`}
                    className="ml-auto shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-emerald-200 text-emerald-700 font-semibold hover:bg-emerald-50 transition-colors">
                    <Download className="w-4 h-4" /> PDF
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}
